import React, { useState, useEffect } from 'react';
import { loansAPI } from '../services/api';
// ...existing code...
import './ManagementPages.css';

const LoansPage = () => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchLoans();
  }, [filter]);

  const fetchLoans = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = filter !== 'all' ? { status: filter.toUpperCase() } : {};
      const response = await loansAPI.getAllLoans(params);
      // Handle paginated response - data is in response.data
      const loansData = response && (Array.isArray(response.data) ? response.data : (response.data?.data || [])) || [];
      // Normalize and compute recover amount + daily EMI for display
      const normalized = (loansData || []).map((l) => {
        const requested = Number(l.requested_amount || l.amount || 0);
        const tenure = Number(l.tenure_days || l.tenureDays || 0) || 0;

        const perLoanRecover = Number(l.recover_amount ?? l.recover_amount_effective ?? 0);
        const ltRecover = Number(l.loan_type_recover_amount ?? l.loan_type_recover ?? 0);
        const ltDaily = Number(l.loan_type_daily_emi ?? l.loan_type_daily ?? 0);
        const explicitDaily = Number(l.daily_emi ?? l.dailyEmi ?? 0);

        // Effective recover amount: prefer per-loan recover, then loan-type recover
        let recoverEffective = perLoanRecover > 0 ? perLoanRecover : ltRecover;
        // If still missing, try derive from explicit daily * tenure
        if ((!recoverEffective || recoverEffective === 0) && explicitDaily > 0 && tenure > 0) {
          recoverEffective = Math.round(explicitDaily * tenure);
        }
        // Fallback to requested amount if nothing else available
        if ((!recoverEffective || recoverEffective === 0) && requested > 0 && tenure > 0) {
          recoverEffective = Math.round(requested);
        }

        // Determine display daily EMI using priority:
        // explicit loan daily -> loan type daily -> recoverEffective / tenure -> requested/tenure -> 0
        let displayDaily = Number.isFinite(explicitDaily) && explicitDaily > 0 ? Math.round(explicitDaily) : 0;
        if ((!displayDaily || displayDaily === 0) && ltDaily > 0) displayDaily = Math.round(ltDaily);
        if ((!displayDaily || displayDaily === 0) && recoverEffective > 0 && tenure > 0) displayDaily = Math.round(recoverEffective / tenure);
        if ((!displayDaily || displayDaily === 0) && requested > 0 && tenure > 0) displayDaily = Math.round(requested / tenure);
        displayDaily = Number.isFinite(Number(displayDaily)) ? displayDaily : 0;
        recoverEffective = Number.isFinite(Number(recoverEffective)) ? recoverEffective : 0;

        return {
          ...l,
          recover_amount_effective: recoverEffective,
          daily_emi: displayDaily,
          loan_type_recover_amount: Number(l.loan_type_recover_amount || 0),
          loan_type_daily_emi: Number(l.loan_type_daily_emi || 0),
          requested_amount: Number(l.requested_amount || l.amount || 0),
          total_payable: Number(l.total_payable || 0),
          collected_amount: Number(l.collected_amount || 0),
        };
      });

      setLoans(normalized);
    } catch (err) {
      setError(err.message || 'Failed to load loans');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (loanId) => {
    try {
      await loansAPI.approve(loanId, { notes: 'Approved' });
      setShowModal(false);
      setSelectedLoan(null);
      fetchLoans();
    } catch (err) {
      alert('Failed to approve: ' + err.message);
    }
  };

  const handleReject = async (loanId) => {
    try {
      const reason = prompt('Enter rejection reason:');
      if (reason) {
        await loansAPI.reject(loanId, { reason });
        setShowModal(false);
        setSelectedLoan(null);
        fetchLoans();
      }
    } catch (err) {
      alert('Failed to reject: ' + err.message);
    }
  };

  const formatCurrency = (amount) => {
    const val = Number(amount);
    const num = Number.isFinite(val) ? val : 0;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(num);
  };

  const safeNumber = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Loans Management</h1>
        <p>Manage and track all loan applications</p>
      </div>

      <div className="filters">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All Loans
        </button>
        <button
          className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Pending
        </button>
        <button
          className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
          onClick={() => setFilter('active')}
        >
          Active
        </button>
        <button
          className={`filter-btn ${filter === 'closed' ? 'active' : ''}`}
          onClick={() => setFilter('closed')}
        >
          Closed
        </button>
        <button
          className={`filter-btn ${filter === 'rejected' ? 'active' : ''}`}
          onClick={() => setFilter('rejected')}
        >
          Rejected
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loader"></div>
      ) : loans.length > 0 ? (
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>Loan ID</th>
                <th>Retailer</th>
                <th>Agent</th>
                <th>Amount</th>
                  <th>Recover</th>
                <th>Daily EMI</th>
                <th>Status</th>
                <th>Tenure</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loans.map((loan) => (
                <tr key={loan.id}>
                  <td>#{loan.id?.slice(0, 8)}</td>
                  <td>{loan.retailer_name || 'N/A'}</td>
                  <td>{loan.agent_first_name} {loan.agent_last_name}</td>
                  <td>{formatCurrency(loan.requested_amount)}</td>
                  <td>{formatCurrency(safeNumber(loan.recover_amount_effective, safeNumber(loan.loan_type_recover_amount)))}</td>
                  <td>{formatCurrency(safeNumber(loan.daily_emi, safeNumber(loan.loan_type_daily_emi)))}</td>
                  <td>
                    <span className={`badge badge-${(loan.status || '').toLowerCase()}`}>
                      {loan.status}
                    </span>
                  </td>
                  <td>{loan.tenure_days} days</td>
                  <td>{loan.created_at ? new Date(loan.created_at).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    <button
                      className="action-btn view-btn"
                      onClick={() => {
                        setSelectedLoan(loan);
                        setShowModal(true);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">No loans found</div>
      )}

      {showModal && selectedLoan && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Loan Details - #{selectedLoan.id?.slice(0, 8)}</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowModal(false);
                  setSelectedLoan(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Loan ID</label>
                  <span>#{selectedLoan.id}</span>
                </div>
                <div className="detail-item">
                  <label>Retailer</label>
                  <span>{selectedLoan.retailer_name}</span>
                </div>
                <div className="detail-item">
                  <label>Shop</label>
                  <span>{selectedLoan.shop_name || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Loan Type</label>
                  <span>{selectedLoan.loan_type_name || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Requested Amount</label>
                  <span>{formatCurrency(selectedLoan.requested_amount)}</span>
                </div>
                <div className="detail-item">
                  <label>Total Payable</label>
                  <span>{formatCurrency(selectedLoan.total_payable)}</span>
                </div>
                <div className="detail-item">
                  <label>Recover Amount</label>
                  <span>{formatCurrency(safeNumber(selectedLoan.recover_amount_effective, safeNumber(selectedLoan.loan_type_recover_amount)))}</span>
                </div>
                <div className="detail-item">
                  <label>Daily EMI</label>
                  <span>{formatCurrency(safeNumber(selectedLoan.daily_emi, safeNumber(selectedLoan.loan_type_daily_emi)))}</span>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <span className={`badge badge-${(selectedLoan.status || '').toLowerCase()}`}>
                    {selectedLoan.status}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Tenure</label>
                  <span>{selectedLoan.tenure_days} days</span>
                </div>
                <div className="detail-item">
                  <label>Collected</label>
                  <span>{formatCurrency(selectedLoan.collected_amount)}</span>
                </div>
                <div className="detail-item">
                  <label>Agent</label>
                  <span>{selectedLoan.agent_first_name} {selectedLoan.agent_last_name}</span>
                </div>
                <div className="detail-item">
                  <label>Created</label>
                  <span>{selectedLoan.created_at ? new Date(selectedLoan.created_at).toLocaleDateString() : 'N/A'}</span>
                </div>
              </div>
              {selectedLoan.admin_notes && (
                <div className="detail-item">
                  <label>Admin Notes</label>
                  <span>{selectedLoan.admin_notes}</span>
                </div>
              )}
              {selectedLoan.rejection_reason && (
                <div className="detail-item">
                  <label>Rejection Reason</label>
                  <span>{selectedLoan.rejection_reason}</span>
                </div>
              )}
            </div>
            {selectedLoan.status === 'PENDING' && (
              <div className="modal-footer">
                <button
                  className="button button-primary"
                  onClick={() => handleApprove(selectedLoan.id)}
                >
                  Approve
                </button>
                <button
                  className="button button-danger"
                  onClick={() => handleReject(selectedLoan.id)}
                >
                  Reject
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedLoan(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoansPage;
